// @flow
import { Formik } from 'formik'
import { useLoadStripeCustomer } from 'queries/payments/useLoadStripeCustomer'
import { useUpdateStripeCustomer } from 'queries/payments/useUpdateStripeCustomer'
import * as React from 'react'
import { toast } from 'react-toastify'
import Button from 'ui/atoms/Button'
import Flex from 'ui/atoms/Flex'
import FlexBox from 'ui/atoms/FlexBox'
import Modal from 'ui/atoms/Modal'
import Space from 'ui/atoms/Space'
import TextField from 'ui/molecules/TextField'
import * as Yup from 'yup'

type Props = {
  onClose: () => void,
}

const BillingDetailsSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Required'),
  address: Yup.object().shape({
    country: Yup.string()
      .transform((value) => (value ? value.toUpperCase() : value))
      .length(2, 'Invalid country code')
      .required('Required'),
    line1: Yup.string().required('Required'),
    line2: Yup.string(),
    city: Yup.string().required('Required'),
    state: Yup.string().required('Required'),
    postal_code: Yup.string()
      .matches(/^\d+$/, 'Zip must contain only numbers')
      .required('Required'),
  }),
})

function EditBillingDetails({ onClose }: Props): React.Node {
  const { data: customer, isLoading: isCustomerLoading } =
    useLoadStripeCustomer()
  const { updateStripeCustomer, isLoading: isUpdateCustomerLoading } =
    useUpdateStripeCustomer()

  const isLoading = isUpdateCustomerLoading || isCustomerLoading

  return (
    <Modal
      show
      onClose={onClose}
      onHide={onClose}
      title="Update Billing Details"
    >
      <FlexBox>
        <Formik
          initialValues={{
            email: customer?.email || '',
            address: {
              city: customer?.address?.city || '',
              line1: customer?.address?.line1 || '',
              line2: customer?.address?.line2 || '',
              state: customer?.address?.state || '',
              country: customer?.address?.country || 'US',
              postal_code: customer?.address?.postal_code || '',
            },
          }}
          isInitialValid={!isCustomerLoading}
          onSubmit={async (values) => {
            await updateStripeCustomer(customer.id, values)
            toast.success('Billing details updated successfully')
            onClose()
          }}
          validationSchema={BillingDetailsSchema}
        >
          {(props) => {
            return (
              <Space size="large">
                <TextField
                  smallLabel
                  useEvent
                  errorLabel={props.errors.email}
                  hasError={props.touched.email && !!props.errors.email}
                  label="Billing Email"
                  name="email"
                  onBlur={props.handleBlur}
                  onChange={props.handleChange}
                  type="email"
                  value={props.values.email}
                />

                <Space>
                  <TextField
                    useEvent
                    errorLabel={props.errors.address?.country}
                    hasError={
                      props.touched.address?.country &&
                      !!props.errors.address?.country
                    }
                    name="address.country"
                    onBlur={props.handleBlur}
                    onChange={props.handleChange}
                    placeholder="Country"
                    value={props.values.address.country}
                  />
                  <TextField
                    useEvent
                    errorLabel={props.errors.address?.line1}
                    hasError={
                      props.touched.address?.line1 &&
                      !!props.errors.address?.line1
                    }
                    name="address.line1"
                    onBlur={props.handleBlur}
                    onChange={props.handleChange}
                    placeholder="Address Line 1"
                    value={props.values.address.line1}
                  />
                  <TextField
                    useEvent
                    name="address.line2"
                    onBlur={props.handleBlur}
                    onChange={props.handleChange}
                    placeholder="Address Line 2"
                    value={props.values.address.line2}
                  />
                  <TextField
                    useEvent
                    errorLabel={props.errors.address?.city}
                    hasError={
                      props.touched.address?.city &&
                      !!props.errors.address?.city
                    }
                    name="address.city"
                    onBlur={props.handleBlur}
                    onChange={props.handleChange}
                    placeholder="City"
                    value={props.values.address.city}
                  />
                  <TextField
                    useEvent
                    errorLabel={props.errors.address?.state}
                    hasError={
                      props.touched.address?.state &&
                      !!props.errors.address?.state
                    }
                    name="address.state"
                    onBlur={props.handleBlur}
                    onChange={props.handleChange}
                    placeholder="State"
                    value={props.values.address.state}
                  />
                  <TextField
                    useEvent
                    errorLabel={props.errors.address?.postal_code}
                    hasError={
                      props.touched.address?.postal_code &&
                      !!props.errors.address?.postal_code
                    }
                    name="address.postal_code"
                    onBlur={props.handleBlur}
                    onChange={props.handleChange}
                    placeholder="Zip"
                    value={props.values.address.postal_code}
                  />
                </Space>

                <Flex $horizontalSpacing="small" justify="end">
                  <Button disabled={isLoading} onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    $primary
                    disabled={isLoading || !props.isValid}
                    loading={isLoading}
                    onClick={props.handleSubmit}
                    type="submit"
                  >
                    Save Changes
                  </Button>
                </Flex>
              </Space>
            )
          }}
        </Formik>
      </FlexBox>
    </Modal>
  )
}

export default EditBillingDetails
